import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { IFlexAction } from 'core-components/sitebuilder/models/flexGrid/IFlexAction'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { selectEditLayoutMode } from 'core-components/sitebuilder/store/selectors/selectEditLayoutMode'
import { selectFlexActions } from 'core-components/sitebuilder/store/selectors/selectFlexActions'
import { setFlexActions } from 'core-components/sitebuilder/store/siteBuilderSlice'

interface IUseFlexActions {
  sitePageId: string
  area?: string
  settings: IFlexLayout
}

export const useFlexActions = ({ settings, sitePageId, area }: IUseFlexActions) => {
  const dispatch = useDispatch()
  const editLayoutMode = useSelector(selectEditLayoutMode)
  const flexActions = useSelector(selectFlexActions)

  const applyActions = (settings: IFlexLayout, actions: IFlexAction[]) => {
    actions.forEach((action) => {
      applyAction(settings, action)
    })
  }

  const applyAction = (settings: IFlexLayout, action: IFlexAction): boolean => {
    for (let i = 0; i < settings.items.length; i++) {
      const current = settings.items[i]
      const next = settings.items[i + 1] || {}
      if (current.name === action.cell1 && next.name === action.cell2) {
        current.size = calc(current.size, action.changes, 1)
        next.size = calc(next.size, action.changes, -1)
        return true
      } else if (current.name === action.cell2 && next.name === action.cell1) {
        current.size = calc(current.size, action.changes, -1)
        next.size = calc(next.size, action.changes, 1)
        return true
      } else if (current.isFlexLayout && current.layoutSet) {
        if (applyAction(current.layoutSet, action)) {
          return true
        }
      }
    }

    return false
  }

  const calc = (
    a: FlexDirectionBreak,
    b: FlexDirectionBreak,
    operator: number
  ): FlexDirectionBreak => {
    return convertToFlexDirectionBreak(convertToNumber(a) + operator * convertToNumber(b))
  }

  const convertToNumber = (a: FlexDirectionBreak): number => {
    switch (a) {
      case FlexDirectionBreak.p112:
        return 1
      case FlexDirectionBreak.p212:
        return 2
      case FlexDirectionBreak.p312:
        return 3
      case FlexDirectionBreak.p412:
        return 4
      case FlexDirectionBreak.p512:
        return 5
      case FlexDirectionBreak.p612:
        return 6
      case FlexDirectionBreak.p712:
        return 7
      case FlexDirectionBreak.p812:
        return 8
      case FlexDirectionBreak.p912:
        return 9
      case FlexDirectionBreak.p1012:
        return 10
      case FlexDirectionBreak.p1112:
        return 11
      case FlexDirectionBreak.p1212:
        return 12
    }
  }

  const convertToFlexDirectionBreak = (a: number): FlexDirectionBreak => {
    if (a < 1) {
      a = 1
    }
    if (a > 12) {
      a = 12
    }
    switch (a) {
      case 1:
        return FlexDirectionBreak.p112
      case 2:
        return FlexDirectionBreak.p212
      case 3:
        return FlexDirectionBreak.p312
      case 4:
        return FlexDirectionBreak.p412
      case 5:
        return FlexDirectionBreak.p512
      case 6:
        return FlexDirectionBreak.p612
      case 7:
        return FlexDirectionBreak.p712
      case 8:
        return FlexDirectionBreak.p812
      case 9:
        return FlexDirectionBreak.p912
      case 10:
        return FlexDirectionBreak.p1012
      case 11:
        return FlexDirectionBreak.p1112
      default:
        return FlexDirectionBreak.p1212
    }
  }

  // Legacy! We need update settings each rerender, because useVisibleSettings reset this value.
  // after fix this problem we can do it on useEffect hook

  if (!editLayoutMode) {
    const actions = flexActions[`${sitePageId}-${area || 0}`] || []
    applyActions(settings, actions)
  }

  useEffect(() => {
    const storageRawValue = localStorage.getItem('sitebuilder__flexActions')
    if (storageRawValue) {
      try {
        const flexActions = JSON.parse(storageRawValue) || {}
        dispatch(setFlexActions(flexActions))
      } catch (err: unknown) {
        console.error(err)
      }
    }
  }, [])
}
